/**
 * AutomationRules.tsx — Feature 24: Automations / Rules
 * A visual rule builder: "When [trigger] Then [action]"
 * Rules are persisted via POST /automations and listed via GET /automations.
 * Frontend-only config UI; the actual rule execution runs server-side.
 */
import React, { useEffect, useState } from 'react';
import { Zap, Plus, X } from 'lucide-react';
import { apiClient, getErrorMessage } from '../packages/api-client';
import { StateView } from '../packages/ui';

type Trigger = 'task_created' | 'task_status_changed' | 'task_overdue' | 'comment_added' | 'task_assigned';
type ActionType = 'send_notification' | 'change_priority' | 'assign_to' | 'add_tag' | 'post_comment';

interface AutomationRule {
    id: string;
    name: string;
    trigger: Trigger;
    trigger_filter?: Record<string, string>;
    action: ActionType;
    action_params: Record<string, string>;
    is_enabled: boolean;
    created_at: string;
}

interface CreateRuleInput {
    name: string;
    trigger: Trigger;
    trigger_filter?: Record<string, string>;
    action: ActionType;
    action_params: Record<string, string>;
}

const TRIGGER_LABELS: Record<Trigger, string> = {
    task_created: 'Task created',
    task_status_changed: 'Status changed',
    task_overdue: 'Task overdue',
    comment_added: 'Comment added',
    task_assigned: 'Task assigned',
};

const ACTION_LABELS: Record<ActionType, string> = {
    send_notification: 'Send notification',
    change_priority: 'Change priority',
    assign_to: 'Assign to user',
    add_tag: 'Add tag',
    post_comment: 'Post comment',
};

export function AutomationRules({ projectId }: { projectId?: string }) {
    const [rules, setRules] = useState<AutomationRule[]>([]);
    const [loading, setLoading] = useState(true);
    // A failed load is not "no automations yet"; a failed action is said, not swallowed.
    const [loadError, setLoadError] = useState('');
    const [actionError, setActionError] = useState('');
    const [reload, setReload] = useState(0);
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState<CreateRuleInput>({
        name: '', trigger: 'task_created', action: 'send_notification', action_params: {},
    });

    const url = projectId ? `/automations?project_id=${projectId}` : '/automations';

    useEffect(() => {
        setLoading(true);
        setLoadError('');
        apiClient.get<{ data: AutomationRule[] }>(url)
            .then(r => setRules(r.data.data ?? []))
            .catch(e => setLoadError(getErrorMessage(e)))
            .finally(() => setLoading(false));
    }, [url, reload]);

    async function handleCreate(e: React.FormEvent) {
        e.preventDefault();
        if (!form.name.trim()) return;
        try {
            // Backend requires project_id; include it only when rendered
            // under a project scope.
            const payload = projectId ? { ...form, project_id: projectId } : form;
            const res = await apiClient.post<AutomationRule>('/automations', payload);
            setRules(prev => [...prev, res.data]);
            setShowCreate(false);
            setForm({ name: '', trigger: 'task_created', action: 'send_notification', action_params: {} });
            setActionError('');
        } catch (err) { setActionError(`Couldn't save the rule: ${getErrorMessage(err)}`); }
    }

    async function toggleEnabled(rule: AutomationRule) {
        try {
            await apiClient.patch(`/automations/${rule.id}`, { is_enabled: !rule.is_enabled });
            setRules(prev => prev.map(r => r.id === rule.id ? { ...r, is_enabled: !r.is_enabled } : r));
            setActionError('');
        } catch (err) { setActionError(`Couldn't change the rule: ${getErrorMessage(err)}`); }
    }

    async function handleDelete(id: string) {
        if (!confirm('Delete this automation?')) return;
        try {
            await apiClient.delete(`/automations/${id}`);
            setRules(prev => prev.filter(r => r.id !== id));
            setActionError('');
        } catch (err) { setActionError(`Couldn't delete the rule: ${getErrorMessage(err)}`); }
    }

    if (loading) return <StateView state="loading" label="Loading automations" />;
    if (loadError) return <StateView state="error" title="Automations couldn't be loaded" message={loadError} onRetry={() => setReload(n => n + 1)} />;

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)' }}>
                <div>
                    <h3 style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--font-size-md)', fontWeight: 700 }}><Zap size={18} aria-hidden="true" /> Automation Rules</h3>
                    <p style={{ margin: '4px 0 0', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>Rules run automatically when their trigger fires.</p>
                </div>
                <button className="btn btn-primary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }} onClick={() => setShowCreate(s => !s)}>
                    {showCreate ? <><X size={14} aria-hidden="true" /> Cancel</> : <><Plus size={14} aria-hidden="true" /> New Rule</>}
                </button>
            </div>

            {actionError && <p role="alert" className="form-error">{actionError}</p>}

            {showCreate && (
                <form onSubmit={handleCreate} style={{ background: 'var(--color-surface-2)', borderRadius: 'var(--radius-md)', padding: 'var(--space-4)', marginBottom: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                    <input className="form-input" required placeholder="Rule name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} autoFocus />
                    <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 200 }}>
                            <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>When</label>
                            <select className="form-input" value={form.trigger} onChange={e => setForm(f => ({ ...f, trigger: e.target.value as Trigger }))}>
                                {(Object.entries(TRIGGER_LABELS) as [Trigger, string][]).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                        </div>
                        <div style={{ flex: 1, minWidth: 200 }}>
                            <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>Then</label>
                            <select className="form-input" value={form.action} onChange={e => setForm(f => ({ ...f, action: e.target.value as ActionType }))}>
                                {(Object.entries(ACTION_LABELS) as [ActionType, string][]).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
                        <button className="btn btn-ghost btn-sm" type="button" onClick={() => setShowCreate(false)}>Cancel</button>
                        <button className="btn btn-primary btn-sm" type="submit">Save Rule</button>
                    </div>
                </form>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                {rules.length === 0 && !showCreate && (
                    <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', textAlign: 'center', padding: 'var(--space-5)', background: 'var(--color-surface-2)', borderRadius: 'var(--radius-md)' }}>
                        No automations yet. Add a rule to automate repetitive actions.
                    </p>
                )}
                {rules.map(rule => (
                    <div key={rule.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-3) var(--space-4)', background: 'var(--color-surface-2)', borderRadius: 'var(--radius-md)', border: `1px solid ${rule.is_enabled ? 'var(--color-border)' : 'var(--color-border)'}`, opacity: rule.is_enabled ? 1 : 0.55 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{rule.name}</div>
                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>
                                {TRIGGER_LABELS[rule.trigger]} → {ACTION_LABELS[rule.action]}
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                            <span style={{ fontSize: 'var(--font-size-xs)', color: rule.is_enabled ? 'var(--color-success)' : 'var(--color-text-muted)', fontWeight: 600 }}>{rule.is_enabled ? 'Active' : 'Paused'}</span>
                            <button className="btn btn-ghost btn-sm" onClick={() => toggleEnabled(rule)}>{rule.is_enabled ? 'Pause' : 'Resume'}</button>
                            <button className="btn btn-ghost btn-sm" aria-label="Delete automation" style={{ color: 'var(--color-danger)' }} onClick={() => handleDelete(rule.id)}><X size={14} aria-hidden="true" /></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
